import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Stack } from 'react-bootstrap';
import DataTable, { TableColumn } from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { selectIsCustomer } from '@components/Auth';

import { InsurancePolicyList } from '@models/InsurancePolicy';
import { InsurancePolicyBadge } from '@components/Feature/InsurancePolicy';
import { routeInsurancePolicyBuyReviewPage } from '@components/InsurancePolicyBuyRoute';
import { tInsuranceTypeEnum } from '@models/InsuranceType';
import { tPolicyType } from '@models/policyType';
import { formatCurrency } from '@utils/currency';
import { formatDate } from '@utils/datetime';

import { selectInsurancePolicyListPageReducer } from './selector';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

const PolicyList = () => {
  const { t } = useTranslation();
  const isCustomer = useSelector(selectIsCustomer);
  const { policies, isLoading, pagination } = useSelector(selectInsurancePolicyListPageReducer);
  const [, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const columns: TableColumn<InsurancePolicyList>[] = [
    {
      cell: ({ id }) => (
        <Stack direction="horizontal" gap={1}>
          <Button
            as={Link}
            to={routeInsurancePolicyBuyReviewPage({ insurancePolicyId: id })}
            variant="outline-primary"
            size="sm"
          >
            {t('common:review')}
          </Button>
        </Stack>
      ),
      button: true,
      center: true,
    },
    {
      name: t('common:policyNumber'),
      selector: ({ policyNumber }) => policyNumber,
      wrap: true,
      minWidth: '150px',
    },
    {
      name: t('common:insuredName'),
      selector: ({ insuredName }) => insuredName ?? '',
      wrap: true,
      minWidth: '150px',
    },
    {
      name: t('common:insuranceType'),
      selector: ({ insuranceType }) => tInsuranceTypeEnum(insuranceType),
      wrap: true,
      minWidth: '130px',
    },
    {
      name: t('common:policyType'),
      selector: ({ policyType }) => tPolicyType(policyType),
      wrap: true,
    },
    {
      name: t('common:insurer'),
      selector: ({ insurer }) => insurer.name,
      wrap: true,
    },
    {
      name: t('common:effectiveDate'),
      selector: ({ effectiveDate }) => formatDate(effectiveDate),
      width: '120px',
    },
    {
      name: t('common:expirationDate'),
      selector: ({ expirationDate }) => formatDate(expirationDate),
      minWidth: '130px',
      wrap: true,
    },
    {
      name: t('common:status'),
      selector: ({ status }) => <InsurancePolicyBadge status={status} />,
    },
    {
      name: t('common:totalPremium'),
      selector: ({ totalPremium }) => formatCurrency(totalPremium),
      right: true,
      minWidth: '120px',
      omit: isCustomer,
    },
    {
      name: t('common:planType'),
      selector: ({ planTypeNames }) => planTypeNames ?? '',
      wrap: true,
      minWidth: '160px',
    },
    {
      name: t('common:frontline'),
      selector: ({ frontline }) => frontline?.name ?? '',
      wrap: true,
      minWidth: '130px',
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={policies ?? []}
      customStyles={customStyles}
      fixedHeader
      pagination
      paginationServer
      progressPending={isLoading}
      paginationPerPage={perPage}
      paginationTotalRows={pagination?.total ?? 0}
      onChangeRowsPerPage={(newPerPage) => setPerPage(newPerPage)}
      onChangePage={(newPage) => setPage(newPage)}
    />
  );
};

export { PolicyList };
